/**
 * Regras compartilhadas do plano "Personalizado" (redes de ensino/secretarias):
 * copy do card, schema de validação do formulário de lead e montagem do payload
 * enviado à Edge Function `submit-custom-plan-request`.
 */
import { z } from 'zod'
import { isValidWhatsAppBR } from './phone'
import type { CustomPlanSource, OrganizationType, TeachersRange } from '../types/customPlanRequests'
import { ORGANIZATION_TYPE_LABELS, ORGANIZATION_TYPES, TEACHERS_RANGE_LABELS, TEACHERS_RANGES } from '../types/customPlanRequests'

export const CUSTOM_PLAN_PATH = '/planos/personalizado'
export type CustomPlanOrigin = 'billing' | 'landing' | 'trial'
export const customPlanPath = (origem: CustomPlanOrigin) => `${CUSTOM_PLAN_PATH}?origem=${origem}`
export const parseCustomPlanSource = (raw: string | null): CustomPlanSource =>
  raw === 'billing' || raw === 'landing' || raw === 'trial' ? raw : 'direct'

// Copy do card — PARA REVISÃO (marketing). Verbatim:
export const CUSTOM_PLAN = {
  name: 'Personalizado',
  eyebrow: 'Para redes de ensino e secretarias',
  priceLabel: 'Sob consulta',
  description: 'Licenças em volume, proposta e implantação feitas sob medida para a sua rede.',
  features: [
    'Tudo do plano Escolar para todas as escolas da rede',
    'Quantidade de licenças definida com você',
    'Proposta comercial e faturamento sob medida',
    'Implantação acompanhada para coordenação e professores',
    'Canal de atendimento dedicado',
  ],
  cta: 'Solicitar proposta',
} as const

export const CUSTOM_PLAN_WA_MESSAGE =
  'Olá! Represento uma rede de ensino/secretaria e quero uma proposta do plano Personalizado da Atividade Pronta.'

// Opções de select derivadas dos labels (mesma ordem de ORGANIZATION_TYPES / TEACHERS_RANGES).
export const ORGANIZATION_TYPE_OPTIONS: ReadonlyArray<{ value: OrganizationType; label: string }> =
  ORGANIZATION_TYPES.map((value) => ({ value, label: ORGANIZATION_TYPE_LABELS[value] }))
export const TEACHERS_RANGE_OPTIONS: ReadonlyArray<{ value: TeachersRange; label: string }> =
  TEACHERS_RANGES.map((value) => ({ value, label: `${TEACHERS_RANGE_LABELS[value]} professores` }))

const selectMsg = { errorMap: () => ({ message: 'Selecione uma opção' }) }

export const customPlanRequestSchema = z.object({
  name: z.string().trim().min(3, 'Informe seu nome completo').max(200, 'Máximo de 200 caracteres'),
  email: z.string().trim().email('Informe um e-mail válido').max(320, 'Máximo de 320 caracteres'),
  phone: z.string().trim().max(30, 'Máximo de 30 caracteres')
    .refine((v) => v === '' || isValidWhatsAppBR(v), 'Informe um telefone válido com DDD')
    .default(''),
  organization: z.string().trim().min(2, 'Informe o nome da rede, secretaria ou instituição').max(200, 'Máximo de 200 caracteres'),
  organization_type: z.enum(['rede_escolas', 'secretaria', 'escola', 'outro'], selectMsg),
  role: z.string().trim().max(120, 'Máximo de 120 caracteres').default(''),
  teachers_range: z.enum(['ate_10', '11_50', '51_200', 'mais_200'], selectMsg),
  message: z.string().trim().max(5000, 'Máximo de 5.000 caracteres').default(''),
  website: z.string().max(0).default(''), // honeypot — deve ficar vazio
})
export type CustomPlanRequestForm = z.infer<typeof customPlanRequestSchema>

export interface CustomPlanRequestPayload {
  name: string
  email: string
  organization: string
  organization_type: OrganizationType
  teachers_range: TeachersRange
  source: CustomPlanSource
  website: string
  phone?: string
  role?: string
  message?: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
  referrer?: string
  captcha_token?: string
}

export function buildCustomPlanRequestPayload(
  form: CustomPlanRequestForm,
  ctx: { source: CustomPlanSource; search: string; referrer: string; captchaToken: string | null }
): CustomPlanRequestPayload {
  const params = new URLSearchParams(ctx.search)
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const
  const utms: Partial<Record<(typeof utmKeys)[number], string>> = {}
  for (const key of utmKeys) {
    const value = params.get(key)
    if (value) utms[key] = value
  }

  return {
    name: form.name,
    email: form.email,
    organization: form.organization,
    organization_type: form.organization_type,
    teachers_range: form.teachers_range,
    source: ctx.source,
    website: form.website,
    ...(form.phone ? { phone: form.phone } : {}),
    ...(form.role ? { role: form.role } : {}),
    ...(form.message ? { message: form.message } : {}),
    ...utms,
    ...(ctx.referrer ? { referrer: ctx.referrer } : {}),
    ...(ctx.captchaToken ? { captcha_token: ctx.captchaToken } : {}),
  }
}
